// The organization's wallet: what it holds, what moved it, what it is made of, and what the
// platform offers to sell it.
//
// Every read here takes `tenancy.billing.manage` — the reads included, which is the backend's own
// gate (`surfaces/app/routes/billing.rs`): a balance, a journal and a bucket list are money facts
// about the tenant. Nothing on this surface is addressed by a customer id: the tenant is
// `X-Org-Id`, which `client.ts` sends.
//
// Every amount is euro cents **excluding tax**. One credit is one cent, so a screen that wants
// euros divides by a hundred and converts nothing.

import { api, withQuery } from '../client'
import type { components, operations } from '../schema'

export type CreditBalanceView = components['schemas']['CreditBalanceView']
export type CreditAllocationEntry = components['schemas']['CreditAllocationEntry']
export type CreditTransactionEntry = components['schemas']['CreditTransactionEntry']
export type CreditTransactionPage = components['schemas']['Page_CreditTransactionEntry']
export type CreditBonusTierEntry = components['schemas']['CreditBonusTierEntry']
export type CreditSpendingView = components['schemas']['CreditSpendingView']
export type CreditSpendingDay = components['schemas']['CreditSpendingDay']

/** The spending read's range: `from` and `to`, `YYYY-MM-DD` UTC, both optional. */
export type CreditSpendingQuery = NonNullable<
  operations['orgs_credit_spending_read']['parameters']['query']
>

/** The journal's closed vocabulary. A word outside it matches nothing rather than being refused. */
export type CreditTransactionKind = NonNullable<CreditTransactionEntry['kind']>

/** The journal walk: `kind`, plus the page envelope's `limit` and `after`. */
export type CreditTransactionsQuery = NonNullable<
  operations['orgs_credit_transactions_list']['parameters']['query']
>

/**
 * The wallet. **404 when the organization's platform declares no credit policy** — there is no
 * wallet at all then, and a screen says so rather than rendering a zero balance.
 */
export function readCredits(signal?: AbortSignal): Promise<CreditBalanceView> {
  return api.get<CreditBalanceView>('/orgs/current/credits', undefined, signal)
}

/**
 * What the wallet spent, per UTC day and per service — the journal's `consumption` lines summed,
 * so the subtitles and the monthly fees are in it beside the meters. Omitted, the range is the
 * current month to today; it covers at most 366 days.
 */
export function readCreditSpending(
  query: CreditSpendingQuery = {},
  signal?: AbortSignal,
): Promise<CreditSpendingView> {
  return api.get<CreditSpendingView>(withQuery('/orgs/current/credits/spending', query), undefined, signal)
}

/**
 * The buckets the balance is made of, in the order consumption drains them — soonest-expiring
 * first, so the month's free euros go before the money paid for. A bucket with nothing left is
 * absent, and their sum is the balance whenever it is not negative.
 */
export function listCreditAllocations(signal?: AbortSignal): Promise<CreditAllocationEntry[]> {
  return api.get<CreditAllocationEntry[]>('/orgs/current/credits/allocations', undefined, signal)
}

/**
 * A page of movements, newest first. There is no `order` and no total: a journal is read from the
 * top, and each line carries the balance the schema stamped on it, so nothing recomputes a running
 * total here.
 */
export function listCreditTransactions(
  query: CreditTransactionsQuery = {},
  signal?: AbortSignal,
): Promise<CreditTransactionPage> {
  return api.get<CreditTransactionPage>(
    withQuery('/orgs/current/credits/transactions', query),
    undefined,
    signal,
  )
}

/**
 * The amounts this platform offers and what each earns, ascending.
 *
 * **This is the source, never a table in the SPA.** `RELOAD_BONUS_TIERS` in the generated pricing
 * catalogue is a new platform's default, and announcing a bonus from it would be announcing one
 * the wallet may not receive — which is the whole reason the figures are published.
 */
export function listCreditBonusTiers(signal?: AbortSignal): Promise<CreditBonusTierEntry[]> {
  return api.get<CreditBonusTierEntry[]>('/orgs/current/credits/bonus-tiers', undefined, signal)
}
